// Webclient → Voicemail. Full inline audio player per message with scrub/seek,
// volume control, live time display, Whisper transcript, summary, search, and download.

import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, fetchAudioUrl } from '../lib/api';
import { useActiveTenant } from '../lib/tenantScope';

type Vm = {
  call_uuid: string; caller: string; callee: string; duration_sec: number;
  started_at: string; heard: boolean; summary: string | null; sentiment: string | null;
  has_transcript: boolean;
};

function fmtWhen(iso: string) {
  try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
}
function fmtDur(sec: number) {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// Download an authed voicemail blob via <a download>
async function downloadVm(vm: Vm) {
  const url = await fetchAudioUrl(`/api/portal/voicemail/${vm.call_uuid}/audio`);
  const a = document.createElement('a');
  a.href = url;
  const date = vm.started_at ? vm.started_at.slice(0, 10) : 'unknown';
  const caller = (vm.caller || 'voicemail').replace(/[^a-z0-9+]/gi, '-');
  a.download = `voicemail-${caller}-${date}.wav`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ── Inline player ─────────────────────────────────────────────────────────────

type PlayerState = { playing: boolean; currentTime: number; duration: number; volume: number; muted: boolean };

function VmPlayer({ vm, onHeard }: { vm: Vm; onHeard: () => void }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [ps, setPs] = useState<PlayerState>({ playing: false, currentTime: 0, duration: vm.duration_sec || 0, volume: 1, muted: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<string | undefined>(undefined);
  const [txLoading, setTxLoading] = useState(false);
  const heardRef = useRef(false);
  const blobRef = useRef<string | null>(null);

  // Load audio blob with auth
  useEffect(() => {
    let cancelled = false;
    fetchAudioUrl(`/api/portal/voicemail/${vm.call_uuid}/audio`)
      .then(url => {
        if (cancelled) { URL.revokeObjectURL(url); return; }
        blobRef.current = url;
        const audio = new Audio(url);
        audio.volume = ps.volume;
        audio.addEventListener('loadedmetadata', () => {
          if (!cancelled) setPs(p => ({ ...p, duration: audio.duration }));
        });
        audio.addEventListener('timeupdate', () => {
          if (!cancelled) setPs(p => ({ ...p, currentTime: audio.currentTime }));
        });
        audio.addEventListener('ended', () => {
          if (!cancelled) { audio.currentTime = 0; setPs(p => ({ ...p, playing: false, currentTime: 0 })); }
        });
        audio.addEventListener('error', () => {
          if (!cancelled) { setError('Playback error'); setPs(p => ({ ...p, playing: false })); }
        });
        audioRef.current = audio;
        setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError(e?.message || 'Could not load audio'); setLoading(false); } });

    return () => {
      cancelled = true;
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; audioRef.current = null; }
      if (blobRef.current) { URL.revokeObjectURL(blobRef.current); blobRef.current = null; }
    };
  }, [vm.call_uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load transcript
  useEffect(() => {
    if (!vm.has_transcript) return;
    setTxLoading(true);
    api.portal.vmTranscript(vm.call_uuid)
      .then(r => setTranscript(r.text || ''))
      .catch(() => setTranscript(''))
      .finally(() => setTxLoading(false));
  }, [vm.call_uuid, vm.has_transcript]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (ps.playing) {
      audio.pause();
      setPs(p => ({ ...p, playing: false }));
    } else {
      audio.play()
        .then(() => {
          setPs(p => ({ ...p, playing: true }));
          if (!heardRef.current) { heardRef.current = true; onHeard(); }
        })
        .catch(e => setError(e?.message || 'Playback error'));
    }
  };

  const seek = (val: number) => {
    const audio = audioRef.current;
    if (audio) audio.currentTime = val;
    setPs(p => ({ ...p, currentTime: val }));
  };

  const skip = (delta: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const t = Math.max(0, Math.min(ps.duration, audio.currentTime + delta));
    audio.currentTime = t;
    setPs(p => ({ ...p, currentTime: t }));
  };

  const setVolume = (v: number) => {
    const audio = audioRef.current;
    if (audio) { audio.volume = v; audio.muted = false; }
    setPs(p => ({ ...p, volume: v, muted: false }));
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    const nowMuted = !ps.muted;
    if (audio) audio.muted = nowMuted;
    setPs(p => ({ ...p, muted: nowMuted }));
  };

  const volPct = ps.muted ? 0 : ps.volume * 100;

  return (
    <div className="bg-elevated/50 border-t border-border/30 px-4 py-3 space-y-3">

      {/* ── Audio controls ── */}
      {error ? (
        <div className="text-xs text-danger py-1">{error}</div>
      ) : loading ? (
        <div className="flex items-center gap-2 py-1">
          <div className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <span className="text-xs text-textSecondary">Loading audio…</span>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Row 1: rewind · play/pause · skip · time · scrub · duration · download */}
          <div className="flex items-center gap-2">

            {/* Rewind 10s */}
            <button onClick={() => skip(-10)} title="Back 10s"
              className="shrink-0 p-1.5 rounded-lg text-textSecondary hover:text-primary hover:bg-bg">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/>
                <text x="8.5" y="14.5" fontSize="5.5" fontFamily="sans-serif" fill="currentColor" stroke="none">10</text>
              </svg>
            </button>

            {/* Play / Pause */}
            <button onClick={togglePlay} title={ps.playing ? 'Pause' : 'Play'}
              className="shrink-0 w-9 h-9 rounded-full bg-primary flex items-center justify-center text-white shadow hover:opacity-90 active:scale-95 transition-transform">
              {ps.playing
                ? <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                : <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M8 5.14v14l11-7-11-7z"/></svg>
              }
            </button>

            {/* Skip 10s */}
            <button onClick={() => skip(10)} title="Forward 10s"
              className="shrink-0 p-1.5 rounded-lg text-textSecondary hover:text-primary hover:bg-bg">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-.49-4.5"/>
                <text x="8.5" y="14.5" fontSize="5.5" fontFamily="sans-serif" fill="currentColor" stroke="none">10</text>
              </svg>
            </button>

            {/* Elapsed */}
            <span className="shrink-0 text-xs text-textSecondary tabular-nums w-9 text-right">{fmtDur(ps.currentTime)}</span>

            {/* Scrub bar */}
            <div className="flex-1 relative flex items-center">
              <input
                type="range" min={0} max={ps.duration || 100} step={0.5}
                value={ps.currentTime}
                onChange={e => seek(parseFloat(e.target.value))}
                className="w-full h-1 rounded-full appearance-none cursor-pointer"
                style={{ accentColor: 'var(--color-primary, #7c5cff)' }}
              />
            </div>

            {/* Duration */}
            <span className="shrink-0 text-xs text-textSecondary tabular-nums w-9">{fmtDur(ps.duration)}</span>

            {/* Download (reuse loaded blob) */}
            <button
              onClick={() => {
                if (blobRef.current) {
                  const a = document.createElement('a');
                  a.href = blobRef.current;
                  const date = vm.started_at ? vm.started_at.slice(0, 10) : 'unknown';
                  const caller = (vm.caller || 'voicemail').replace(/[^a-z0-9+]/gi, '-');
                  a.download = `voicemail-${caller}-${date}.wav`;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                } else { downloadVm(vm); }
              }}
              title="Download voicemail"
              className="shrink-0 p-1.5 rounded-lg text-textSecondary hover:text-primary hover:bg-bg">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
            </button>
          </div>

          {/* Row 2: volume */}
          <div className="flex items-center gap-2 pl-1">
            <button onClick={toggleMute} title={ps.muted ? 'Unmute' : 'Mute'}
              className="shrink-0 p-1 text-textSecondary hover:text-primary">
              {volPct === 0
                ? <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                : volPct < 50
                ? <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                : <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
              }
            </button>
            <input
              type="range" min={0} max={1} step={0.05}
              value={ps.muted ? 0 : ps.volume}
              onChange={e => setVolume(parseFloat(e.target.value))}
              className="w-20 h-1 rounded-full appearance-none cursor-pointer"
              style={{ accentColor: 'var(--color-primary, #7c5cff)' }}
              title={`Volume ${Math.round(volPct)}%`}
            />
            <span className="text-xs text-textSecondary tabular-nums">{Math.round(volPct)}%</span>
          </div>
        </div>
      )}

      {/* ── Transcript ── */}
      {(vm.has_transcript || transcript !== undefined) && (
        <div className="pt-2 border-t border-border/30">
          <div className="text-xs font-medium text-textSecondary uppercase tracking-wide mb-1.5">Transcript</div>
          {txLoading
            ? <div className="text-xs text-textSecondary">Loading transcript…</div>
            : transcript
            ? <div className="text-sm text-text leading-relaxed whitespace-pre-wrap">{transcript}</div>
            : <div className="text-xs text-textSecondary italic">No transcript available.</div>
          }
        </div>
      )}

      {/* ── AI summary ── */}
      {vm.summary && (
        <div className="pt-1 border-t border-border/30">
          <div className="text-xs font-medium text-textSecondary uppercase tracking-wide mb-1">Summary</div>
          <div className="text-sm text-textSecondary italic">{vm.summary}</div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Voicemail() {
  const qc = useQueryClient();
  const [openUuid, setOpenUuid] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState<string | null>(null);
  const tenant = useActiveTenant();
  const vms = useQuery({ queryKey: ['portal-vms', tenant], queryFn: () => api.portal.voicemails() });

  const del = useMutation({
    mutationFn: (uuid: string) => api.portal.vmDelete(uuid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-vms'] });
      setOpenUuid(null);
    },
  });

  const markHeard = (uuid: string) => {
    api.portal.vmHeard(uuid)
      .then(() => qc.invalidateQueries({ queryKey: ['portal-vms'] }))
      .catch(() => {});
  };

  const handleDownload = async (e: React.MouseEvent, vm: Vm) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(vm.call_uuid);
    try { await downloadVm(vm); } finally { setDownloading(null); }
  };

  const toggle = (uuid: string) => setOpenUuid(p => p === uuid ? null : uuid);

  const q = search.trim().toLowerCase();
  const visible: Vm[] = (vms.data || []).filter((vm: Vm) =>
    !q || (vm.caller || '').toLowerCase().includes(q) || (vm.callee || '').toLowerCase().includes(q)
  );

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Header with search */}
      <div className="shrink-0 flex items-center gap-3 px-4 h-12 border-b border-border">
        <span className="font-semibold text-sm shrink-0">Voicemail</span>
        <div className="flex-1 relative">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-textSecondary pointer-events-none" viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="search" placeholder="Search caller…"
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-7 pr-3 py-1 text-xs bg-elevated rounded-lg border border-border/50 focus:outline-none focus:border-primary text-text placeholder:text-textSecondary"
          />
        </div>
        {search && (
          <span className="shrink-0 text-xs text-textSecondary">{visible.length} result{visible.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {vms.isLoading && <div className="p-4 text-sm text-textSecondary">Loading…</div>}
        {!vms.isLoading && visible.length === 0 && (
          <div className="p-4 text-sm text-textSecondary">{search ? 'No matching voicemails.' : 'No voicemail.'}</div>
        )}
        {visible.map((vm: Vm) => {
          const open = openUuid === vm.call_uuid;
          return (
            <div key={vm.call_uuid} className="border-b border-border/50">
              {/* Row — click anywhere (except action icons) to open/close player */}
              <button
                onClick={() => toggle(vm.call_uuid)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${open ? 'bg-elevated' : 'hover:bg-elevated/60'}`}
              >
                {/* Unread dot */}
                <span className={`shrink-0 w-2 h-2 rounded-full ${vm.heard ? 'bg-transparent' : 'bg-primary'}`} />

                {/* Play/pause glyph */}
                <span className={`shrink-0 p-1.5 rounded-full border ${open ? 'border-primary text-primary bg-primary/10' : 'border-border text-textSecondary'}`}>
                  {open
                    ? <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                    : <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M8 5.14v14l11-7-11-7z"/></svg>
                  }
                </span>

                {/* Caller + meta */}
                <div className="min-w-0 flex-1">
                  <div className={`text-sm truncate ${vm.heard ? 'font-normal' : 'font-semibold'}`}>
                    {vm.caller || 'Unknown'}
                  </div>
                  <div className="text-xs text-textSecondary truncate">
                    {vm.summary || `Voicemail · ${fmtDur(vm.duration_sec)}`}
                  </div>
                </div>

                {/* Duration */}
                <span className="shrink-0 text-xs text-textSecondary tabular-nums">{fmtDur(vm.duration_sec)}</span>

                {/* Date */}
                <span className="shrink-0 text-xs text-textSecondary">{fmtWhen(vm.started_at)}</span>

                {/* Download */}
                <span
                  role="button" tabIndex={0}
                  onClick={e => handleDownload(e, vm)}
                  onKeyDown={e => { if (e.key === 'Enter') handleDownload(e as any, vm); }}
                  title="Download voicemail"
                  className={`shrink-0 p-1.5 rounded-lg text-textSecondary hover:text-primary hover:bg-bg ${downloading === vm.call_uuid ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {downloading === vm.call_uuid
                    ? <div className="w-3.5 h-3.5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                    : <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                      </svg>
                  }
                </span>

                {/* Delete */}
                <span
                  role="button" tabIndex={0}
                  onClick={e => { e.stopPropagation(); if (confirm('Delete this voicemail?')) del.mutate(vm.call_uuid); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); if (confirm('Delete this voicemail?')) del.mutate(vm.call_uuid); } }}
                  title="Delete"
                  className="shrink-0 p-1.5 rounded-lg text-textSecondary hover:text-danger hover:bg-bg"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                  </svg>
                </span>
              </button>

              {open && <VmPlayer vm={vm} onHeard={() => markHeard(vm.call_uuid)} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
